import React, { KeyboardEvent, ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import Container from '@ui/container/container';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import Text from '@ui/text/text/text';

interface WatermarkPctgInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  min?: number;
  disabled?: boolean;
}

const WatermarkPctgInput: React.FC<WatermarkPctgInputProps> = ({
  value,
  onChange,
  label = 'Text',
  min = 1,
  disabled,
}) => {
  const [displayValue, setDisplayValue] = useState(value);
  const isFocused = useRef(false);

  useEffect(() => {
    if (!isFocused.current) {
      setDisplayValue(value);
    }
  }, [value]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const raw = e.target.value;
      const digits = raw.replace(/\D/g, '');
      setDisplayValue(digits);

      const numericValue = parseInt(digits, 10);
      if (!isNaN(numericValue)) {
        onChange(String(numericValue));
      }
    },
    [onChange]
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();

        const step = e.shiftKey ? 10 : 1;
        const currentNum = parseInt(displayValue, 10) || 0;
        const nextValue = e.key === 'ArrowUp' ? currentNum + step : currentNum - step;
        const clamped = String(Math.max(min, nextValue));

        onChange(clamped);
        setDisplayValue(clamped);
      }
    },
    [displayValue, min, onChange]
  );

  const handleFocus = useCallback(() => {
    isFocused.current = true;
  }, []);

  const handleBlur = useCallback(() => {
    isFocused.current = false;
    const num = parseInt(displayValue, 10);
    const clamped = String(isNaN(num) || num < min ? min : num);
    onChange(clamped);
    setDisplayValue(clamped);
  }, [displayValue, min, onChange]);

  const renderInner = useCallback(
    () => (
      <Container className="flex flex-1 jc-flex-end">
        <Text className="P2" color="var(--global-ico-text-3)">
          %
        </Text>
      </Container>
    ),
    []
  );

  return (
    <InputWithOverlay
      jsxLabel={() => (
        <Text className="L2 padding-l-16 margin-b-2" design="GlobalIcoText3">
          {label}
        </Text>
      )}
      inputHeight="36px"
      innerContentClassName="width-100-percent padding-h-12"
      enableChildrenPointerEvents={false}
      inputWidth="100%"
      wrapperClass="flex-1"
      inputProps={{
        autoComplete: 'off',
        name: `${label.toLowerCase()}-watermark`,
        type: 'text',
        inputMode: 'numeric',
        maxLength: 4,
        value: displayValue,
        onKeyDown,
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        className: 'P2',
        disabled,
        style: disabled ? { opacity: 0.6 } : undefined,
      }}
      renderInnerContent={renderInner}
    />
  );
};

export default WatermarkPctgInput;
